/**
 * Server Export Client
 * 
 * Client-side module to interact with the server-side export service.
 * Handles job submission, status polling via Firestore, and download.
 */

import { getFirestore, doc, onSnapshot, getDoc, Unsubscribe } from 'firebase/firestore';
import { Clip, Track, SubtitleStyle } from './types';

// ============================================================================
// TYPES
// ============================================================================

import { flattenTimeline, getClipsForChunk } from './utils/timeline-utils';

export interface ExportJobConfig {
    projectId: string;
    clips: Clip[];
    tracks: Track[];
    scaleMode?: 'fit' | 'fill';
    activeSubtitleLanguage?: string;
    subtitleStyle?: SubtitleStyle;
    quality: 'draft' | 'standard' | 'high';
    resolution?: { width: number; height: number };
    burnSubtitles?: boolean;  // Hard-code subtitles into video (slower but always visible)
    engine?: 'mlt' | 'ffmpeg';
    // Hybrid Chunk properties
    chunkIndex?: number;
    totalChunks?: number;
    chunkStartTime?: number;
    chunkEndTime?: number;
    outputKey?: string;
    cutPoints?: number[];
}

export interface ExportJobStatus {
    jobId: string;
    status: 'pending' | 'downloading' | 'processing' | 'encoding' | 'uploading' | 'complete' | 'failed';
    progress: number;
    currentStep?: string;
    createdAt: number;
    startedAt?: number;
    completedAt?: number;
    downloadUrl?: string;
    error?: string;
    totalChunks?: number;
}

type StatusCallback = (status: ExportJobStatus) => void;

// ============================================================================
// CONFIGURATION
// ============================================================================

// Export service URL (Cloud Run) - configure in environment or .env
const EXPORT_SERVICE_URL = import.meta.env?.VITE_EXPORT_SERVICE_URL
    || 'https://export-service-400620783493.us-central1.run.app';

const buildOutputKey = (userId: string, fileName: string): string =>
    `LVMH/outputs/${userId}/${fileName}`;

// ============================================================================
// CLIENT CLASS
// ============================================================================

export class ServerExportClient {
    private db = getFirestore();
    private userId: string;
    private activeSubscriptions = new Map<string, Unsubscribe>();

    constructor(userId: string) {
        console.log('[ServerExportClient] Initializing v2.2 (Queue Optimization)');
        this.userId = userId;
    }

    /**
     * Submit a new export job
     * Returns the job ID immediately, processing happens in background
     */
    async submitExportJob(config: ExportJobConfig): Promise<string> {
        const jobId = this.generateJobId();

        console.log('[ServerExportClient] Submitting export job:', jobId);

        const preparedClips = config.clips.map(clip => ({
            ...clip,
            // Remove non-serializable properties
            thumbnails: undefined,
            waveform: undefined,
        }));

        // Use shared utility
        const flattenedClips = flattenTimeline(preparedClips);

        const requestBody = {
            jobId,
            projectId: config.projectId,
            userId: this.userId,
            clips: flattenedClips,
            tracks: config.tracks,
            scaleMode: config.scaleMode,
            activeSubtitleLanguage: config.activeSubtitleLanguage,
            subtitleStyle: config.subtitleStyle,
            format: 'mp4' as const,
            quality: config.quality,
            resolution: config.resolution,
            burnSubtitles: config.burnSubtitles ?? false,
            engine: config.engine,
            chunkIndex: config.chunkIndex,
            totalChunks: config.totalChunks,
            chunkStartTime: config.chunkStartTime,
            chunkEndTime: config.chunkEndTime,
            outputKey: config.outputKey,
        };

        try {
            const endpoint = config.engine === 'mlt' ? `${EXPORT_SERVICE_URL}/export-mlt` : `${EXPORT_SERVICE_URL}/export`;
            const response = await fetch(endpoint, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(requestBody),
            });

            // Accept both 200 (sync complete) and 202 (async started)
            if (response.status === 202 || response.ok) {
                console.log('[ServerExportClient] Job accepted:', jobId);
                return jobId;
            }

            const error = await response.json();
            throw new Error(error.error || 'Export submission failed');

        } catch (error) {
            console.error('[ServerExportClient] Failed to submit job:', error);
            throw error;
        }
    }

    /**
     * Submit export job AND subscribe to status updates in parallel
     * Returns jobId immediately, callback receives real-time progress
     * This prevents UI freeze by not waiting for HTTP response before polling
     */
    submitAndSubscribe(
        config: ExportJobConfig,
        callback: StatusCallback
    ): { jobId: string; unsubscribe: () => void } {
        const jobId = this.generateJobId();
        console.log('[ServerExportClient] submitAndSubscribe:', jobId);

        // Start Firestore polling IMMEDIATELY (before HTTP request)
        const unsubscribe = this.subscribeToJobStatus(jobId, callback);

        // Send HTTP request in background (don't await)
        this.sendExportRequest(jobId, config).catch(err => {
            console.error('[ServerExportClient] Background submit failed:', err);
            callback({
                jobId,
                status: 'failed',
                progress: 0,
                createdAt: Date.now(),
                error: err.message || 'Failed to submit job',
            });
        });

        return { jobId, unsubscribe };
    }

    /**
     * Internal: Send export request to server with resilient retry
     * Creates Firestore doc first (so job appears in queue), then calls server
     */
    private async sendExportRequest(jobId: string, config: ExportJobConfig): Promise<void> {
        const preparedClips = config.clips.map(clip => ({
            ...clip,
            thumbnails: undefined,
            waveform: undefined,
        }));

        const flattenedClips = flattenTimeline(preparedClips);

        const requestBody = {
            jobId,
            projectId: config.projectId,
            userId: this.userId,
            clips: flattenedClips,
            tracks: config.tracks,
            scaleMode: config.scaleMode,
            activeSubtitleLanguage: config.activeSubtitleLanguage,
            subtitleStyle: config.subtitleStyle,
            format: 'mp4' as const,
            quality: config.quality,
            resolution: config.resolution,
            burnSubtitles: config.burnSubtitles ?? false,
            engine: config.engine,
            chunkIndex: config.chunkIndex,
            totalChunks: config.totalChunks,
            chunkStartTime: config.chunkStartTime,
            chunkEndTime: config.chunkEndTime,
            outputKey: config.outputKey,
        };

        const endpoint = config.engine === 'mlt' ? `${EXPORT_SERVICE_URL}/export-mlt` : `${EXPORT_SERVICE_URL}/export`;
        await this.sendQueuedExportRequest(jobId, config.projectId, flattenedClips, config, requestBody, endpoint);
    }

    private async sendQueuedExportRequest(
        jobId: string,
        projectId: string,
        flattenedClips: Clip[],
        config: ExportJobConfig,
        requestBody: Record<string, unknown>,
        endpoint: string
    ): Promise<void> {
        // Create the Firestore doc before touching Cloud Run so polling/retries have state immediately.
        const { doc: firestoreDoc, setDoc, Timestamp, updateDoc } = await import('firebase/firestore');
        const jobDocRef = firestoreDoc(this.db, 'export-jobs', jobId);
        await setDoc(jobDocRef, {
            userId: this.userId,
            projectId,
            status: 'queued',
            progress: 0,
            currentStep: 'En file d\'attente (client)...',
            createdAt: Timestamp.now(),
            config: {
                format: 'mp4',
                quality: config.quality,
                engine: config.engine || 'ffmpeg',
                clipCount: flattenedClips.length,
                totalDuration: Math.max(...flattenedClips.map((c: any) => c.start + c.duration), 0),
                chunkIndex: config.chunkIndex ?? null,
                totalChunks: config.totalChunks ?? null,
            }
        }, { merge: true });
        console.log('[ServerExportClient] Firestore job doc created:', jobId);

        const MAX_RETRIES = 5;
        const BASE_DELAY = 30000; // 30s, 60s, 120s, 240s, 480s
        for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
            try {
                const response = await fetch(endpoint, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(requestBody),
                });

                if (response.ok || response.status === 202) {
                    console.log('[ServerExportClient] Export request accepted by server');
                    return;
                }

                if (response.status === 429 && attempt < MAX_RETRIES) {
                    const delay = BASE_DELAY * Math.pow(2, attempt); // 30s, 60s, 120s, 240s, 480s
                    console.log(`[ServerExportClient] Server busy (429), retry ${attempt + 1}/${MAX_RETRIES} in ${delay / 1000}s...`);

                    // Update Firestore status to show we're waiting
                    await updateDoc(jobDocRef, {
                        currentStep: `Serveur occupé, retry ${attempt + 1}/${MAX_RETRIES} dans ${delay / 1000}s...`
                    }).catch(() => { });

                    await new Promise(resolve => setTimeout(resolve, delay));
                    continue;
                }

                // Other error
                const error = await response.json().catch(() => ({ error: `HTTP ${response.status}` }));
                throw new Error(error.error || `Export submission failed (${response.status})`);

            } catch (err: any) {
                if (err.message?.includes('Failed to fetch') || err.name === 'TypeError') {
                    // Network/CORS error (likely 429 before CORS headers) — retry
                    if (attempt < MAX_RETRIES) {
                        const delay = BASE_DELAY * Math.pow(2, attempt);
                        console.log(`[ServerExportClient] Network error (likely 429+CORS), retry ${attempt + 1}/${MAX_RETRIES} in ${delay / 1000}s...`);
                        await updateDoc(jobDocRef, {
                            currentStep: `Serveur occupé (CORS), retry ${attempt + 1}/${MAX_RETRIES} dans ${delay / 1000}s...`
                        }).catch(() => { });
                        await new Promise(resolve => setTimeout(resolve, delay));
                        continue;
                    }
                }
                throw err;
            }
        }

        console.log('[ServerExportClient] All retries exhausted, marking job as failed:', jobId);
        await updateDoc(jobDocRef, {
            status: 'failed',
            progress: 0,
            currentStep: 'Échec: Cloud Run inaccessible ou saturé',
            error: 'Export submission failed after all retries'
        }).catch(() => { });
        throw new Error('Export submission failed after all retries');
    }


    /**
     * Subscribe to job status updates via Firestore
     * Returns unsubscribe function
     */
    subscribeToJobStatus(jobId: string, callback: StatusCallback): () => void {
        console.log('[ServerExportClient] Subscribing to job status:', jobId);

        const jobRef = doc(this.db, 'export-jobs', jobId);

        const unsubscribe = onSnapshot(jobRef, (snapshot) => {
            if (snapshot.exists()) {
                const data = snapshot.data();
                callback({
                    jobId,
                    status: data.status || 'pending',
                    progress: data.progress || 0,
                    currentStep: data.currentStep,
                    createdAt: data.createdAt?.toMillis?.() || Date.now(),
                    startedAt: data.startedAt?.toMillis?.(),
                    completedAt: data.completedAt?.toMillis?.(),
                    downloadUrl: data.downloadUrl,
                    error: data.error,
                });
            }
        }, (error) => {
            console.error('[ServerExportClient] Firestore subscription error:', error);
            callback({
                jobId,
                status: 'failed',
                progress: 0,
                createdAt: Date.now(),
                error: 'Failed to get job status',
            });
        });

        this.activeSubscriptions.set(jobId, unsubscribe);

        return () => {
            unsubscribe();
            this.activeSubscriptions.delete(jobId);
        };
    }

    /**
     * Get current job status (one-time fetch)
     */
    async getJobStatus(jobId: string): Promise<ExportJobStatus | null> {
        try {
            const jobRef = doc(this.db, 'export-jobs', jobId);
            const snapshot = await getDoc(jobRef);

            if (!snapshot.exists()) {
                return null;
            }

            const data = snapshot.data();
            return {
                jobId,
                status: data.status || 'pending',
                progress: data.progress || 0,
                currentStep: data.currentStep,
                createdAt: data.createdAt?.toMillis?.() || Date.now(),
                startedAt: data.startedAt?.toMillis?.(),
                completedAt: data.completedAt?.toMillis?.(),
                downloadUrl: data.downloadUrl,
                error: data.error,
            };
        } catch (error) {
            console.error('[ServerExportClient] Failed to get job status:', error);
            return null;
        }
    }

    /**
 * Cancel a running job — actually cancels server-side + Firestore
 */
    async cancelJob(jobId: string): Promise<void> {
        console.log('[ServerExportClient] Cancelling job:', jobId);

        // Unsubscribe from updates
        const unsub = this.activeSubscriptions.get(jobId);
        if (unsub) {
            unsub();
            this.activeSubscriptions.delete(jobId);
        }

        try {
            // 1. Update Firestore directly (instant, works even if server is busy)
            const { doc, updateDoc, deleteDoc, getFirestore } = await import('firebase/firestore');
            const db = getFirestore();

            // Cancel the main job doc
            await updateDoc(doc(db, 'export-jobs', jobId), {
                status: 'cancelled',
                currentStep: 'Annulé par l\'utilisateur',
            });

            // Release the global lock
            await deleteDoc(doc(db, 'export-jobs', '_global_lock')).catch(() => { });

            console.log('[ServerExportClient] Job cancelled in Firestore + lock released');

            // 2. Also notify the server (best effort, non-blocking)
            fetch(`${EXPORT_SERVICE_URL}/cancel/${jobId}`, { method: 'POST' }).catch(() => { });
        } catch (err) {
            console.error('[ServerExportClient] Cancel error:', err);
        }
    }
    async getActiveJobs(): Promise<ExportJobStatus[]> {
        // This would query Firestore for all pending/processing jobs
        // Implementation depends on your query needs
        return [];
    }

    /**
     * Trigger video ingestion (chunking + transcription)
     * Called after video upload to R2. Splits video into 5-min chunks,
     * uploads to Stream + R2, and transcribes each chunk in parallel.
     * 
     * @param sourceUrl - R2 URL of the uploaded video
     * @param projectId - Current project ID
     * @param assetId - Clip/asset ID
     * @param onProgress - Optional progress callback
     * @returns Ingestion result with chunk info and merged transcript
     */
    async triggerIngestion(
        sourceUrl: string,
        projectId: string,
        assetId: string,
        onProgress?: (status: string, progress: number) => void
    ): Promise<{
        success: boolean;
        chunks?: Array<{
            index: number;
            streamMediaId: string;
            streamUrl: string;
            r2Url: string;
            startTime: number;
            duration: number;
            transcript: Array<{ start: number; end: number; text: string }>;
        }>;
        mergedTranscript?: Array<{ start: number; end: number; text: string }>;
        language?: string;
        error?: string;
    }> {
        console.log(`[ServerExportClient] Triggering ingestion for asset ${assetId}`);
        onProgress?.('Démarrage de l\'ingestion...', 0);

        try {
            const response = await this.fetchWithRetry(`${EXPORT_SERVICE_URL}/ingest/process`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    sourceUrl,
                    projectId,
                    userId: this.userId,
                    assetId,
                }),
            }, 2, 1000); // 2 retries, 1s backoff

            if (!response.ok) {
                const errorText = await response.text();
                throw new Error(`Ingestion failed: ${errorText}`);
            }

            const result = await response.json();
            console.log(`[ServerExportClient] Ingestion complete: ${result.chunks?.length || 0} chunks`);
            onProgress?.('Ingestion terminée!', 100);

            // AUTO-MERGE: Flatten transcripts from all chunks into a single timeline-aligned transcript
            let mergedTranscript: Array<{ start: number; end: number; text: string }> = [];
            if (result.chunks) {
                // Ensure chunks are sorted by index
                const sortedChunks = result.chunks.sort((a: any, b: any) => a.index - b.index);

                // Map chunks to their transcripts (which are already timeline-adjusted by the backend)
                mergedTranscript = sortedChunks.flatMap((chunk: any) => chunk.transcript || []);
            }

            return {
                ...result,
                success: true, // Force success as we passed HTTP status check
                mergedTranscript
            };

        } catch (error) {
            console.error('[ServerExportClient] Ingestion failed:', error);
            return {
                success: false,
                error: error instanceof Error ? error.message : String(error)
            };
        }
    }

    /**
     * Upload a video to Cloudflare Stream directly (without chunking/transcription)
     * Useful when client-side transcription succeeded but we still want HLS streaming.
     */
    async uploadToStream(
        sourceUrl: string,
        assetId: string,
        onProgress?: (status: string, progress: number) => void
    ): Promise<{ success: boolean; streamUrl?: string; streamMediaId?: string; error?: string }> {
        console.log(`[ServerExportClient] Triggering Stream upload for asset ${assetId}`);
        onProgress?.('Démarrage de l\'upload Stream...', 0);

        try {
            const response = await this.fetchWithRetry(`${EXPORT_SERVICE_URL}/stream/upload`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    sourceUrl,
                    userId: this.userId,
                    assetId,
                }),
            }, 2, 1000);

            if (!response.ok) {
                const errorText = await response.text();
                throw new Error(`Stream upload failed: ${errorText}`);
            }

            const result = await response.json();
            console.log(`[ServerExportClient] Stream upload initiated: ${result.streamUrl}`);
            onProgress?.('Envoi au stream terminé!', 100);

            return result;

        } catch (error) {
            console.error('[ServerExportClient] Stream upload failed:', error);
            return {
                success: false,
                error: error instanceof Error ? error.message : String(error)
            };
        }
    }

    /**
     * Save subtitles to R2 storage
     * Returns the public URL of the saved subtitles
     */
    async saveSubtitles(
        projectId: string,
        clipId: string,
        subtitles: Record<string, Array<{ id: string | number; start: number; end: number; text: string }>>
    ): Promise<string | null> {
        try {
            const response = await this.fetchWithRetry(`${EXPORT_SERVICE_URL}/subtitles/save`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    userId: this.userId,
                    projectId,
                    clipId,
                    subtitles,
                }),
            }, 2, 1000);

            if (!response.ok) {
                console.error('[ServerExportClient] Failed to save subtitles:', await response.text());
                return null;
            }

            const result = await response.json();
            console.log(`[ServerExportClient] Subtitles saved: ${result.url}`);
            return result.url;
        } catch (error) {
            console.error('[ServerExportClient] Error saving subtitles:', error);
            return null;
        }
    }

    /**
     * Load subtitles from R2 storage
     */
    async loadSubtitles(
        projectId: string,
        clipId: string
    ): Promise<Record<string, Array<{ id: string | number; start: number; end: number; text: string }>> | null> {
        try {
            const response = await fetch(`${EXPORT_SERVICE_URL}/subtitles/${this.userId}/${projectId}/${clipId}`);

            if (!response.ok) {
                if (response.status === 404) {
                    console.log(`[ServerExportClient] No subtitles found for clip ${clipId}`);
                    return null;
                }
                console.error('[ServerExportClient] Failed to load subtitles:', await response.text());
                return null;
            }

            return await response.json();
        } catch (error) {
            console.error('[ServerExportClient] Error loading subtitles:', error);
            return null;
        }
    }

    /**
     * Save raw transcript text to R2 storage
     * Returns the public URL of the saved transcript
     */
    async saveTranscript(
        projectId: string,
        clipId: string,
        text: string
    ): Promise<string | null> {
        try {
            const response = await this.fetchWithRetry(`${EXPORT_SERVICE_URL}/transcript/save`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    userId: this.userId,
                    projectId,
                    clipId,
                    text,
                }),
            }, 2, 1000);

            if (!response.ok) {
                console.error('[ServerExportClient] Failed to save transcript:', await response.text());
                return null;
            }

            const result = await response.json();
            console.log(`[ServerExportClient] Transcript saved: ${result.url}`);
            return result.url;
        } catch (error) {
            console.error('[ServerExportClient] Error saving transcript:', error);
            return null;
        }
    }

    /**
     * Submit a chunked export for long videos
     * Splits timeline into 5-minute chunks, processes in parallel, then concatenates
     */
    async submitChunkedExport(
        config: ExportJobConfig,
        onProgress: (chunksDone: number, totalChunks: number, phase: 'processing' | 'merging') => void
    ): Promise<string> {
        const TARGET_CHUNK_DURATION = config.engine === 'mlt' ? 120 : 300; // Turbo MLT: ~2min chunks, FFmpeg: ~5min
        const CUT_TOLERANCE = 30; // Look for subtitle ends within ±30s of target

        // Calculate total duration from clips
        const flattenedClips = flattenTimeline(config.clips);
        const totalDuration = Math.max(...flattenedClips.map(c => c.start + c.duration), 0);

        // Collect all subtitle end times from all clips
        // Support mixed timing bases (timeline/source/relative).
        const allSubtitleEnds: number[] = [];

        const inWindow = (value: number, min: number, max: number, tolerance = 0.25): boolean =>
            value >= (min - tolerance) && value <= (max + tolerance);

        const detectSubtitleTimingMode = (
            clip: Clip,
            subs: Array<{ start: number; end: number; text: string }>
        ): 'timeline' | 'source' | 'relative' => {
            const clipStart = clip.start;
            const clipEnd = clip.start + clip.duration;
            const trimStart = clip.trimStart || 0;
            const trimEnd = trimStart + clip.duration;

            let timelineVotes = 0;
            let sourceVotes = 0;
            let relativeVotes = 0;

            for (const sub of subs) {
                if (!Number.isFinite(sub.start)) continue;
                if (inWindow(sub.start, clipStart, clipEnd)) timelineVotes++;
                if (inWindow(sub.start, trimStart, trimEnd)) sourceVotes++;
                if (inWindow(sub.start, 0, clip.duration)) relativeVotes++;
            }

            if (timelineVotes >= sourceVotes && timelineVotes >= relativeVotes) return 'timeline';
            if (sourceVotes >= relativeVotes) return 'source';
            return 'relative';
        };

        for (const clip of flattenedClips) {
            if (clip.subtitles) {
                for (const subs of Object.values(clip.subtitles)) {
                    const timingMode = detectSubtitleTimingMode(clip, subs as Array<{ start: number; end: number; text: string }>);
                    for (const sub of subs) {
                        if (!Number.isFinite(sub.end)) continue;
                        const timelineEnd = timingMode === 'timeline'
                            ? sub.end
                            : timingMode === 'source'
                                ? clip.start + (sub.end - clip.trimStart)
                                : clip.start + sub.end;
                        allSubtitleEnds.push(timelineEnd);
                    }
                }
            }
        }
        allSubtitleEnds.sort((a, b) => a - b);

        // Use shared utility (imported from timelines-utils) or inline logic
        // But for consistency we should refactor strict usage here.
        // For now, let's inject the single-chunk submission method below.
        const explicitCutPoints = (config.cutPoints || [])
            .map(value => Number(value))
            .filter(value => Number.isFinite(value))
            .map(value => Math.max(0, Math.min(totalDuration, value)));

        const chunkBoundaries = explicitCutPoints.length >= 2
            ? [...new Set([0, ...explicitCutPoints, totalDuration])]
                .sort((a, b) => a - b)
                .filter((value, index, list) => index === 0 || value - list[index - 1] >= 0.25)
            : (() => {
                const smartBoundaries: number[] = [0];
                let currentTime = 0;

                while (currentTime + TARGET_CHUNK_DURATION < totalDuration) {
                    const targetCut = currentTime + TARGET_CHUNK_DURATION;

                    // Find the best subtitle end near the target cut point
                    const nearbyEnds = allSubtitleEnds.filter(
                        t => t > targetCut - CUT_TOLERANCE && t < targetCut + CUT_TOLERANCE
                    );

                    // Pick the one closest to target, or use target if no subtitles nearby
                    const bestCut = nearbyEnds.length > 0
                        ? nearbyEnds.reduce((best, t) =>
                            Math.abs(t - targetCut) < Math.abs(best - targetCut) ? t : best
                        )
                        : targetCut;

                    smartBoundaries.push(bestCut);
                    currentTime = bestCut;
                }
                smartBoundaries.push(totalDuration);
                return smartBoundaries;
            })();

        const numChunks = chunkBoundaries.length - 1;
        console.log(`[ChunkedExport] ${explicitCutPoints.length >= 2 ? 'Timeline cut chunking' : 'Smart chunking'}: ${totalDuration}s = ${numChunks} chunks`);
        console.log(`[ChunkedExport] Boundaries: ${chunkBoundaries.map(t => Math.round(t)).join(', ')}s`);

        if (numChunks <= 1) {
            // Short video, use regular export
            console.log('[ChunkedExport] Video too short for chunking, using regular export');
            return this.submitExportJob(config);
        }

        // GENERATE BASE JOB ID ONCE
        // This ensures all chunks and the final merge share a related ID structure
        const baseJobId = this.generateJobId();
        console.log(`[ChunkedExport] Starting parallel export with BASE_ID: ${baseJobId}`);

        const chunkUrls: string[] = new Array(numChunks).fill(null);
        // Explicitly track missing indices for debugging
        const pendingIndices = new Set(Array.from({ length: numChunks }, (_, i) => i));

        const errorDetails: string[] = [];
        let completedChunksCount = 0;

        // CONCURRENCY CONTROL: Warmup + Turbo Mode
        // MLT jobs are heavier per chunk, so a lower parallelism often yields better real throughput.
        const CONCURRENCY_LIMIT = config.engine === 'mlt' ? 6 : 10;
        const STAGGER_MS = 0; // No delay - we warmup first
        const chunkEndpoint = config.engine === 'mlt'
            ? `${EXPORT_SERVICE_URL}/export-mlt`
            : `${EXPORT_SERVICE_URL}/export`;

        // Warmup ping to ensure server is ready before parallel requests
        try {
            const warmupResponse = await fetch(`${EXPORT_SERVICE_URL}/health`, {
                method: 'GET',
                headers: { 'Content-Type': 'application/json' },
            });
            if (warmupResponse.ok) console.log(`[ChunkedExport] Server warm! Starting parallel export.`);
        } catch (warmupErr) {
            console.warn(`[ChunkedExport] Warmup ping failed (likely CORS or cold start), proceeding.`, warmupErr);
        }

        const tasks = Array.from({ length: numChunks }, (_, i) => i);
        const executing = new Set<Promise<void>>();

        console.log(`[ChunkedExport] Starting processing queue. Concurrency: ${CONCURRENCY_LIMIT}`);

        for (const chunkIndex of tasks) {
            // Processing logic for a single chunk
            const task = (async () => {
                const startTime = chunkBoundaries[chunkIndex];
                const endTime = chunkBoundaries[chunkIndex + 1];

                // DETERMINISTIC CHUNK ID
                const chunkJobId = `${baseJobId}_chunk${chunkIndex}`;

                console.log(`[ChunkedExport] Submitting chunk ${chunkIndex + 1}/${numChunks} [${chunkJobId}]: ${startTime.toFixed(1)}s - ${endTime.toFixed(1)}s`);

                // Filter clips that overlap with this chunk
                const chunkClips = getClipsForChunk(flattenedClips, startTime, endTime);

                const requestBody = {
                    jobId: chunkJobId,
                    projectId: config.projectId,
                    userId: this.userId,
                    engine: config.engine || 'ffmpeg',
                    clips: chunkClips.map(clip => ({
                        ...clip,
                        thumbnails: undefined,
                        waveform: undefined,
                    })),
                    tracks: config.tracks,
                    activeSubtitleLanguage: config.activeSubtitleLanguage,
                    subtitleStyle: config.subtitleStyle,
                    format: 'mp4' as const,
                    quality: config.quality,
                    resolution: config.resolution,
                    burnSubtitles: config.burnSubtitles ?? false,
                    chunkIndex: chunkIndex,
                    totalChunks: numChunks,
                    chunkStartTime: startTime,
                    chunkEndTime: endTime,
                    outputKey: buildOutputKey(this.userId, `chunks/${chunkJobId}.mp4`),
                };

                try {
                    // Use retry logic for robustness against transient network/CORS errors
                    const response = await this.fetchWithRetry(chunkEndpoint, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify(requestBody),
                    });

                    if (!response.ok) {
                        const errText = await response.text();
                        throw new Error(`Chunk ${chunkIndex} failed status ${response.status}: ${errText}`);
                    }

                    const result = await response.json();

                    // CRITICAL: Verify we got a download URL
                    if (!result.downloadUrl) {
                        throw new Error(`Chunk ${chunkIndex} completed but returned no downloadUrl`);
                    }

                    // Store result in correct index (thread-safe in JS event loop)
                    chunkUrls[chunkIndex] = result.downloadUrl;
                    pendingIndices.delete(chunkIndex);

                    completedChunksCount++;
                    onProgress(completedChunksCount, numChunks, 'processing');

                } catch (error) {
                    console.error(`Chunk ${chunkIndex} failed:`, error);
                    errorDetails.push(`Chunk ${chunkIndex} (${chunkJobId}): ${error instanceof Error ? error.message : String(error)}`);
                }
            })();

            executing.add(task);
            task.then(() => executing.delete(task));

            if (executing.size >= CONCURRENCY_LIMIT) {
                await Promise.race(executing);
            }

            if (errorDetails.length > 0) {
                console.error('[ChunkedExport] Aborting remaining chunks due to error');
                break;
            }
        }

        // Wait for all tasks
        await Promise.all(executing);

        if (errorDetails.length > 0) {
            throw new Error(`Export failed. Errors:\n${errorDetails.join('\n')}`);
        }

        // VERIFY INTEGRITY
        // Ensure NO undefined slots in chunkUrls
        for (let i = 0; i < numChunks; i++) {
            if (!chunkUrls[i]) {
                throw new Error(`Missing chunk at index ${i} (ID: ${baseJobId}_chunk${i}). Download URL not received.`);
            }
        }

        console.log(`[ChunkedExport] All ${numChunks} chunks successfully collected. Starting merge...`);
        onProgress(numChunks, numChunks, 'merging');

        // FINAL MERGE STEP
        // Use the baseJobId for the final file, or a strict suffix
        const concatJobId = `${baseJobId}_final`;

        console.log(`[ChunkedExport] Requesting CONCAT for job ${concatJobId} with ${chunkUrls.length} segments`);

        const concatResponse = await fetch(`${EXPORT_SERVICE_URL}/export/concat`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                jobId: concatJobId,
                projectId: config.projectId,
                userId: this.userId,
                chunkUrls: chunkUrls, // Order is guaranteed by array index assignment
                deleteChunks: true,   // Request cleanup of intermediate chunks
                outputKey: buildOutputKey(this.userId, `${baseJobId}.mp4`)
            }),
        });

        if (!concatResponse.ok) {
            const errText = await concatResponse.text();
            throw new Error(`Merge failed (Status ${concatResponse.status}): ${errText}`);
        }

        const concatResult = await concatResponse.json();
        console.log(`[ChunkedExport] Merge complete: ${concatResult.downloadUrl}`);

        return concatJobId;
    }

    /**
     * Download the exported file
     */
    async downloadExport(jobId: string): Promise<void> {
        const status = await this.getJobStatus(jobId);

        if (!status || status.status !== 'complete' || !status.downloadUrl) {
            throw new Error('Export not ready for download');
        }

        // Trigger download
        const link = document.createElement('a');
        link.href = status.downloadUrl;
        link.download = `export_${jobId}.mp4`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    }

    /**
     * Submit a burn-subtitles-parallel export for simple timelines.
     * Uses the fast seek-mode parallel endpoint: splits at subtitle gaps,
     * workers use HTTP Range seeking on the original URL (no pre-download).
     * 
     * @param videoUrl - R2 URL of the video
     * @param srtContent - SRT subtitle content string
     * @param projectId - Project ID for Firestore tracking
     * @param onProgress - Progress callback (status text + percent)
     * @returns Download URL of the final exported video
     */
    async submitBurnSubtitlesParallel(
        videoUrl: string,
        srtContent: string,
        projectId: string,
        onProgress: (status: string, progress: number) => void
    ): Promise<string> {
        const jobId = this.generateJobId();
        console.log(`[BurnParallel] Starting job ${jobId}`);

        // 1. Upload SRT to R2 first
        onProgress('Upload des sous-titres...', 5);
        const srtBlob = new Blob([srtContent], { type: 'text/plain' });
        const srtUrl = await this.saveSubtitlesSRT(srtBlob, projectId, jobId);
        if (!srtUrl) throw new Error('Failed to upload SRT');

        // 2. Send request to /burn-subtitles-parallel
        onProgress('Lancement export parallèle...', 10);
        const response = await this.fetchWithRetry(`${EXPORT_SERVICE_URL}/burn-subtitles-parallel`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                jobId,
                videoUrl,
                subtitleUrl: srtUrl,
                userId: this.userId,
                projectId
            })
        });

        if (!response.ok) {
            const errorText = await response.text();
            throw new Error(`Burn parallel failed: ${response.status} ${errorText}`);
        }

        // 3. Subscribe to Firestore for progress updates
        return new Promise<string>((resolve, reject) => {
            const unsub = this.subscribeToJobStatus(jobId, (status) => {
                const progress = status.progress || 0;
                const step = status.currentStep || status.status || '';
                onProgress(step, progress);

                if (status.status === 'complete' && status.downloadUrl) {
                    unsub();
                    resolve(status.downloadUrl);
                } else if (status.status === 'failed') {
                    unsub();
                    reject(new Error(status.error || 'Export failed'));
                }
            });
        });
    }

    /**
     * Submit an asynchronous chunk concatenation job to Cloud Run.
     * Starts the merging process on the server back-end and polls until completion.
     * Prevents browser Out-Of-Memory (OOM) crashes by bypassing FFmpeg.wasm for large files.
     */
    async submitAsyncConcatJob(
        projectId: string,
        chunkUrls: string[],
        outputKey?: string
    ): Promise<string> {
        const jobId = this.generateJobId();

        console.log(`[ServerExportClient] submitAsyncConcatJob started: ${jobId} with ${chunkUrls.length} chunks`);

        const response = await fetch(`${EXPORT_SERVICE_URL}/export/concat-async`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                jobId,
                projectId,
                userId: this.userId,
                chunkUrls,
                outputKey
            })
        });

        if (!response.ok) {
            const errorText = await response.text();
            throw new Error(`submitAsyncConcatJob failed: ${response.status} ${errorText}`);
        }

        // Subscribe to Firestore for progress updates until complete
        return new Promise<string>((resolve, reject) => {
            const unsub = this.subscribeToJobStatus(jobId, (status) => {
                if (status.status === 'complete' && status.downloadUrl) {
                    unsub();
                    resolve(status.downloadUrl);
                } else if (status.status === 'failed') {
                    unsub();
                    reject(new Error(status.error || 'Server concat failed'));
                }
            });
        });
    }

    /**
     * Submit burn-parallel WITH per-chunk tracking for client-side merge.
     * Same as submitBurnSubtitlesParallel but:
     * - Tracks individual chunk statuses via Firestore fields (chunk_N_status, chunk_N_url)
     * - Fires onChunkUpdate for each chunk as it completes on the server
     * - Resolves when ALL chunks are server-complete (does NOT wait for server merge)
     * - Caller is responsible for downloading chunks and merging client-side
     */
    async submitBurnParallelWithChunks(
        videoUrl: string,
        srtContent: string,
        projectId: string,
        onChunkUpdate: (chunkIndex: number, serverStatus: string, serverProgress: number, chunkUrl: string | null) => void,
        onProgress: (status: string, progress: number) => void,
        subtitleStyle?: any,
        cutPoints?: number[]
    ): Promise<{ jobId: string; totalChunks: number }> {
        const jobId = this.generateJobId();
        console.log(`[BurnParallelChunks] Starting job ${jobId}`);
        // ... rest of implementation (using existing code)
        // 1. Upload SRT to R2
        onProgress('Upload des sous-titres...', 5);
        const srtBlob = new Blob([srtContent], { type: 'text/plain' });
        const srtUrl = await this.saveSubtitlesSRT(srtBlob, projectId, jobId);
        if (!srtUrl) throw new Error('Failed to upload SRT');

        // 2. Subscribe to Firestore FIRST (before fetch) so we don't block the UI if the server is waiting for a slot
        return new Promise<{ jobId: string; totalChunks: number }>((resolve, reject) => {
            let resolvedTotalChunks = 0;
            let resolved = false;

            const jobRef = doc(this.db, 'export-jobs', jobId);
            const unsub = onSnapshot(jobRef, (snapshot) => {
                if (!snapshot.exists()) return;
                const data = snapshot.data();

                const totalChunks = data?.totalChunks || 0;

                // If it's waiting in queue, pass the message to UI
                if (totalChunks === 0) {
                    if (data?.currentStep) {
                        onProgress(data.currentStep, data.progress || 10);
                    }
                    return;
                }

                let chunksDone = 0;
                let chunksFailed = 0;
                let chunksEncoding = 0;
                let totalChunkProgress = 0;

                for (let ci = 0; ci < totalChunks; ci++) {
                    const cStatus = data?.[`chunk_${ci}_status`] || 'pending';
                    const cProgress = data?.[`chunk_${ci}_progress`] || 0;
                    const cUrl = data?.[`chunk_${ci}_url`] || null;

                    if (cStatus === 'complete') { chunksDone++; totalChunkProgress += 100; }
                    else if (cStatus === 'failed') { chunksFailed++; }
                    else if (cStatus === 'encoding') { chunksEncoding++; totalChunkProgress += cProgress; }

                    // Fire callback for each chunk
                    onChunkUpdate(ci, cStatus, cProgress, cUrl);
                }

                // Progress
                const avgProgress = totalChunks > 0 ? totalChunkProgress / totalChunks : 0;
                const computedProgress = 15 + Math.round(avgProgress * 0.65);
                const statusMsg = `⚙️ ${chunksDone}/${totalChunks} ✅${chunksEncoding > 0 ? ` | ${chunksEncoding} en cours` : ''}`;
                onProgress(statusMsg, computedProgress);

                // All failed
                if (chunksFailed > 0) {
                    const settled = chunksDone + chunksFailed;
                    if (settled >= totalChunks && !resolved) {
                        resolved = true;
                        unsub();
                        reject(new Error(`${chunksFailed}/${totalChunks} chunk(s) échoué(s)`));
                    }
                }

                // All complete → resolve immediately (don't wait for server merge!)
                if (chunksDone === totalChunks && totalChunks > 0 && !resolved) {
                    resolved = true;
                    resolvedTotalChunks = totalChunks;
                    unsub();
                    resolve({ jobId, totalChunks: resolvedTotalChunks });
                }

                // Hard failure
                if (data?.status === 'failed' && !resolved) {
                    resolved = true;
                    unsub();
                    reject(new Error(data.error || 'Export failed'));
                }
            });

            // 3. Fire-and-forget request to /burn-subtitles-parallel in the background
            onProgress('Lancement export parallèle...', 10);
            this.fetchWithRetry(`${EXPORT_SERVICE_URL}/burn-subtitles-parallel`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    jobId,
                    videoUrl,
                    subtitleUrl: srtUrl,
                    userId: this.userId,
                    projectId,
                    subtitleStyle,
                    cutPoints
                })
            }).then(async response => {
                if (!response.ok) {
                    const errorText = await response.text();
                    const errMsg = `Burn parallel failed: ${response.status} ${errorText}`;
                    console.error(errMsg);
                    if (!resolved) {
                        resolved = true;
                        unsub();
                        reject(new Error(errMsg));
                    }
                }
            }).catch(err => {
                console.error("[BurnParallelChunks] Network/Timeout error:", err);
                // We don't necessarily reject here because if it's a timeout, the server might still be processing.
                // The Firestore listener will eventually catch the timeout failure from the server side if it actually fails.
            });
        });
    }

    /**
     * Submit a SINGLE chunk export job (used by True Hybrid / SmartHybridExporter)
     * Uses fire-and-forget + Firestore polling.
     * Return the jobId immediately, caller must poll getJobStatus() for completion.
     */
    async submitSingleChunkJob(
        chunkJobId: string,
        config: ExportJobConfig,
        chunkGlobalIndex: number,
        totalChunks: number,
        startTime: number,
        endTime: number,
        outputKey: string
    ): Promise<{ chunkJobId: string, fetchPromise: Promise<void> }> {
        // Prepare and flatten clips (same as sendExportRequest)
        const preparedClips = config.clips.map(clip => ({
            ...clip,
            thumbnails: undefined,
            waveform: undefined,
        }));

        const flattenedClips = flattenTimeline(preparedClips);

        const requestBody = {
            jobId: chunkJobId,
            projectId: config.projectId,
            userId: this.userId,
            clips: flattenedClips,
            tracks: config.tracks,
            activeSubtitleLanguage: config.activeSubtitleLanguage,
            subtitleStyle: config.subtitleStyle,
            format: 'mp4' as const,
            quality: config.quality,
            resolution: config.resolution,
            burnSubtitles: config.burnSubtitles ?? false,
            chunkIndex: chunkGlobalIndex,
            totalChunks: totalChunks,
            chunkStartTime: startTime,
            chunkEndTime: endTime,
            outputKey: outputKey,
        };

        // We use the ASYNC endpoint so Cloud Run returns 200 Accepted immediately.
        // True Hybrid relies on Firestore polling to discover when the chunk is actually finished.
        // This avoids Cloud Run 429 timeouts and 60-second HTTP limits for long chunks.
        const fetchPromise = this.fetchWithRetry(
            `${EXPORT_SERVICE_URL}/export-async`,
            {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(requestBody),
            }
        ).then(async res => {
            if (!res.ok) {
                const text = await res.text();
                throw new Error(`Cloud Run Async Dispatch Failed: ${res.status} ${text}`);
            }
        });

        return { chunkJobId, fetchPromise };
    }

    /**
     * Upload raw SRT content to R2 and return the public URL.
     */
    private async saveSubtitlesSRT(srtBlob: Blob, projectId: string, jobId: string): Promise<string | null> {
        try {
            const { httpsCallable } = await import('firebase/functions');
            const { getFunctions } = await import('firebase/functions');
            const functions = getFunctions();
            const getR2UploadUrl = httpsCallable(functions, 'getR2UploadUrl');
            const result = await getR2UploadUrl({
                fileName: `subtitles_${jobId}.srt`,
                contentType: 'text/plain'
            }) as any;
            const { uploadUrl, publicUrl } = result.data;

            await fetch(uploadUrl, {
                method: 'PUT',
                body: srtBlob,
                headers: { 'Content-Type': 'text/plain' }
            });

            return publicUrl;
        } catch (err) {
            console.error('[BurnParallel] Failed to upload SRT:', err);
            return null;
        }
    }

    /**
     * Cleanup all subscriptions
     */
    cleanup(): void {
        for (const [jobId, unsub] of this.activeSubscriptions) {
            unsub();
        }
        this.activeSubscriptions.clear();
    }

    // ============================================================================
    // HELPERS
    // ============================================================================

    private generateJobId(): string {
        return `export_${this.userId.slice(0, 8)}_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    }

    /**
     * Helper to retry fetches with exponential backoff.
     * Useful for parallel requests that might hit rate limits or cold-start timeouts (CORS).
     */
    private async fetchWithRetry(url: string, options: RequestInit, retries = 5, backoff = 1000): Promise<Response> {
        try {
            const response = await fetch(url, options);

            // Retry on 5xx server errors or 429 rate limits
            if (!response.ok && (response.status >= 500 || response.status === 429)) {
                // If 429 (Cloud Run queue full / scaling up), we want to try again heavily.
                if (response.status === 429) {
                    console.log(`[ServerExportClient] 🚦 429 Too Many Requests hit for ${options.body ? JSON.parse(options.body as string).chunkIndex : 'unknown chunk'}. Queuing request...`);
                }
                throw new Error(`Server returned ${response.status}`);
            }

            return response;
        } catch (error) {
            if (retries <= 0) {
                console.error(`[ServerExportClient] Fetch failed after all retries:`, error);
                throw error;
            }

            // Jittered exponential backoff (prevents thundering herd problem)
            const jitter = Math.random() * 500;
            const delay = backoff + jitter;

            console.warn(`[ServerExportClient] Fetch failed, retrying in ${Math.round(delay)}ms... (${retries} retries left)`, error);
            await new Promise(r => setTimeout(r, delay));

            // For 429s, we cap the backoff at 8 seconds so we don't wait forever, but we give Cloud Run time to spin up instances
            const nextBackoff = Math.min(backoff * 1.5, 8000);
            return this.fetchWithRetry(url, options, retries - 1, nextBackoff);
        }
    }
}

// ============================================================================
// SINGLETON INSTANCE
// ============================================================================

let clientInstance: ServerExportClient | null = null;

export function getServerExportClient(userId: string): ServerExportClient {
    if (!clientInstance || clientInstance['userId'] !== userId) {
        clientInstance = new ServerExportClient(userId);
    }
    return clientInstance;
}

// ============================================================================
// HELPER: Should use server-side export?
// ============================================================================

/**
 * Determine if server-side export should be used based on project complexity
 */
export function shouldUseServerExport(clips: Clip[], tracks: Track[]): boolean {
    // Calculate total duration
    const totalDuration = Math.max(...clips.map(c => c.start + c.duration), 0);

    // Count video clips
    const videoClipCount = clips.filter(c =>
        c.type === 'video' &&
        tracks.find(t => t.id === c.trackId)?.isVisible
    ).length;

    // Check for special effects that benefit from server processing
    const hasSpeedChanges = clips.some(c => c.playbackRate && c.playbackRate !== 1.0);
    const hasIntroOutro = clips.some(c => c.clipRole === 'intro' || c.clipRole === 'outro');
    const hasSubtitles = clips.some(c => c.subtitles && Object.keys(c.subtitles).length > 0);

    // Criteria for server-side export:
    // - Total duration > 10 minutes
    // - More than 20 video clips
    // - Has speed changes (complex filter)
    // - Has intro/outro with alpha
    // - Has subtitles

    if (totalDuration > 600) { // 10 minutes
        console.log('[ServerExportClient] Using server export: duration > 10min');
        return true;
    }

    if (videoClipCount > 20) {
        console.log('[ServerExportClient] Using server export: > 20 clips');
        return true;
    }

    if (hasSpeedChanges) {
        console.log('[ServerExportClient] Using server export: has speed changes');
        return true;
    }

    if (hasIntroOutro) {
        console.log('[ServerExportClient] Using server export: has intro/outro');
        return true;
    }

    if (hasSubtitles) {
        console.log('[ServerExportClient] Using server export: has subtitles');
        return true;
    }

    return false;
}

// ============================================================================
